import { UUID } from '@litlingo/client';
import SelectRedesign from 'components/Select/SelectRedesign';
import { CLOSE_ICON_12 } from 'constants/commonIcons';
import { SEARCH_ICON } from 'constants/filterIcons';
import React, { useState } from 'react';
import {
  getChangedLanguageMatchers,
  getCompareMode,
  getIdentifierCustomers,
  getSelectedCustomerIdentifier,
  getShouldDisableEdit,
} from 'selectors/identifier';
import { useSelector } from 'store';
import type { MLanguageMatcher, NormalizedIdentifier } from 'types';
import LanguageMatcherSidebarItem from './LanguageMatcherSidebarItem';

type ComponentProps = {
  languageMatchers: MLanguageMatcher[];
  languageMatcherFilter: string;
  highlightedLanguageMatcher: string | undefined;
  setLanguageMatcherFilter: React.Dispatch<React.SetStateAction<string>>;
  addLocalLanguageMatcher: () => void;
  handleLanguageMatcherClick: (languageMatcherId: UUID) => void;
  identifierRevision: NormalizedIdentifier;
};

const LanguageMatcherSidebarList: React.FC<ComponentProps> = ({
  languageMatchers,
  languageMatcherFilter,
  highlightedLanguageMatcher,
  setLanguageMatcherFilter,
  addLocalLanguageMatcher,
  handleLanguageMatcherClick,
  identifierRevision,
}) => {
  const selectedCustomer = useSelector(getSelectedCustomerIdentifier);
  const changedLanguageMatchers = useSelector(getChangedLanguageMatchers);
  const compareMode = useSelector(getCompareMode);
  const shouldDisable = useSelector(getShouldDisableEdit);
  const customers = useSelector(getIdentifierCustomers);

  const [isSearching, setIsSearching] = useState(false);
  const [customerFilter, setCustomerFilter] = useState('');

  const handleFilterChange = (value: string): void => {
    setLanguageMatcherFilter(value);
  };

  const handleToggleSearch = (): void => {
    setIsSearching(!isSearching);
  };

  const handleSelectCustomer = (customer: string): void => {
    setCustomerFilter(customer);
  };

  let options = customers.map((c) => ({ label: c.name || '', value: c.uuid }));
  options = [{ label: 'All Workspaces', value: '' }, ...options];

  const selectedCustomerData = customers.find((c) => c.uuid === selectedCustomer);

  return (
    <div className="w-full flex flex-col relative text-body">
      <div className="w-full px-4 py-2 flex flex-row items-center gap-1 border-b border-litlingo-gray-1">
        {isSearching ? (
          <div className=" flex flex-row w-full">
            <div className="flex flex-row w-full">
              <input
                id="search"
                name="search"
                className="border-l border-t border-b border-litlingo-gray-2 h-8 w-full text-litlingo-gray-6 pl-4 text-body placeholder-italic placeholder-litlingo-gray-6 rounded-l"
                placeholder="Search Matchers and Terms"
                onChange={(e): void => handleFilterChange(e.target.value)}
                value={languageMatcherFilter}
                autoComplete="off"
                data-testid="search-input"
              />
              <button
                type="button"
                className="focus:outline-none border-t border-b border-r border-litlingo-gray-2 pr-2"
                onClick={(): void => {
                  handleToggleSearch();
                  handleFilterChange('');
                }}
              >
                {CLOSE_ICON_12}
              </button>
            </div>
            <div className="flex items-center px-1 rounded-r border-t border-b border-r border-litlingo-gray-2 w-7 ">
              <button type="button" className="focus:outline-none w-5" onClick={handleToggleSearch}>
                {SEARCH_ICON()}
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-1 flex-row gap-2.5 justify-between items-center">
            {selectedCustomerData ? (
              <div className="w-52 italic truncate">
                <b>View: {selectedCustomerData.name}</b>
              </div>
            ) : (
              <div className="w-54">
                <SelectRedesign
                  onChange={handleSelectCustomer}
                  options={options}
                  value={customerFilter || ''}
                  className="bg-white"
                />
              </div>
            )}

            <button
              type="button"
              className="w-7 h-8 p-1 border border-litlingo-gray-2 rounded focus:outline-none"
              onClick={handleToggleSearch}
              data-testid="search-button"
            >
              {SEARCH_ICON()}
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={addLocalLanguageMatcher}
          className="button button--secondary h-8"
          data-testid="add-matcher-button"
          disabled={compareMode || shouldDisable}
        >
          <span className="font-bold">Add</span>
        </button>
      </div>

      <div className="flex flex-col w-full">
        {compareMode && languageMatchers.length > 0 && (
          <div className="relative flex flex-col w-full">
            {languageMatchers
              .filter((l) => changedLanguageMatchers?.includes(l.uuid))
              .map((languageMatcher) => (
                <LanguageMatcherSidebarItem
                  key={languageMatcher.uuid}
                  languageMatcher={languageMatcher}
                  handleLanguageMatcherClick={handleLanguageMatcherClick}
                  highlightedLanguageMatcher={highlightedLanguageMatcher}
                  identifierRevision={identifierRevision}
                  selectedCustomer={selectedCustomer || customerFilter}
                />
              ))}
          </div>
        )}
        {compareMode && changedLanguageMatchers?.length > 0 && (
          <div className="w-full h-6 py-0.5 flex flex-row justify-center items-center gap-1 text-small bg-litlingo-highlight">
            <span>Changes made above</span>
          </div>
        )}
        <div className="relative flex flex-col w-full">
          {languageMatchers.length > 0 ? (
            languageMatchers
              .filter((l) => !changedLanguageMatchers?.includes(l.uuid) || !compareMode)
              .map((languageMatcher) => (
                <LanguageMatcherSidebarItem
                  key={languageMatcher.uuid}
                  languageMatcher={languageMatcher}
                  handleLanguageMatcherClick={handleLanguageMatcherClick}
                  highlightedLanguageMatcher={highlightedLanguageMatcher}
                  identifierRevision={identifierRevision}
                  selectedCustomer={selectedCustomer || customerFilter}
                />
              ))
          ) : (
            <div className="flex justify-center py-6">
              <span className="text-sm font-semibold">No language matchers to show</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LanguageMatcherSidebarList;
